import { type ApiResponse, post } from "@src/appV2/api";
import { fullAddressSchema, geoLocationSchema, locationSchema } from "@src/appV2/lib/address/types";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { useGetWorker } from "@src/appV2/Worker/api/useGetWorker";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import timezoneLookup from "tz-lookup";
import { z } from "zod";

const addressSchema = fullAddressSchema.partial();

export const requestSchema = z.object({
  address: addressSchema,
  location: locationSchema,
  tmz: z.string().optional(),
});

export const responseSchema = z.object({
  address: addressSchema,
  geoLocation: geoLocationSchema,
  _id: z.string(),
});

export type UpdateAddressRequest = Omit<z.infer<typeof requestSchema>, "tmz">;
export type UpdateAddressResponse = z.infer<typeof responseSchema>;

export function useUpdateAddress(
  options: UseMutationOptions<
    ApiResponse<UpdateAddressResponse>,
    AxiosError,
    UpdateAddressRequest
  > = {}
): UseMutationResult<ApiResponse<UpdateAddressResponse>, AxiosError, UpdateAddressRequest> {
  const { refetch: refetchWorker } = useGetWorker({ enabled: false });

  return useMutation({
    mutationFn: async (data) => {
      const tmz = timezoneLookup(data.location.lat, data.location.lng);

      const result = await post({
        url: "/agentProfile/address",
        data: { ...data, tmz },
        requestSchema,
        responseSchema,
      });

      // Refetch worker to get latest data after successful update
      await refetchWorker();

      return result;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.UPDATE_ADDRESS_ERROR,
    },
    ...options,
  });
}
